import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Trash, UploadSimple } from '@phosphor-icons/react';

/**
 * Signature capture pad. Users can draw with mouse/finger OR upload a
 * transparent PNG. Emits a base64 PNG string via onChange whenever the
 * signature is finalised (mouse-up / touch-end) or an image is uploaded.
 */
export default function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // High-DPI scale
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#0a1b3a';
  }, []);

  const posFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = posFromEvent(e);
    setEmpty(false);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = posFromEvent(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    emit();
  };

  const emit = () => {
    const canvas = canvasRef.current;
    onChange && onChange(canvas.toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setEmpty(true);
    onChange && onChange('');
  };

  const onUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // fit image into canvas, preserve aspect
        const scale = Math.min(rect.width / img.width, rect.height / img.height);
        const w = img.width * scale;
        const h = img.height * scale;
        ctx.drawImage(img, (rect.width - w) / 2, (rect.height - h) / 2, w, h);
        setEmpty(false);
        emit();
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="signature-pad" className="border-2 border-black bg-white p-4 brutal-shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-500">Draw or upload your signature</p>
        <div className="flex items-center gap-2">
          <label className="brutal-btn secondary text-xs inline-flex items-center gap-1 cursor-pointer" data-testid="signature-upload-btn">
            <UploadSimple weight="bold" size={14} />
            Upload
            <input
              type="file"
              accept="image/*"
              hidden
              data-testid="signature-file-input"
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
          </label>
          <button
            type="button"
            onClick={clear}
            className="brutal-btn secondary text-xs inline-flex items-center gap-1"
            data-testid="signature-clear-btn"
            disabled={empty}
          >
            <Trash weight="bold" size={14} /> Clear
          </button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="signature-canvas"
        className="w-full h-48 bg-zinc-50 border-2 border-dashed border-zinc-300 touch-none"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        style={{ cursor: 'crosshair' }}
      />
      {empty && (
        <p className="text-xs text-zinc-500 mt-2 font-mono-plex">
          Sign inside the box with your mouse or finger. On mobile, hold your device sideways for more room.
        </p>
      )}
    </div>
  );
}
